import React from 'react';
import { Column, TableInstance, ColumnInstance } from 'react-table';

import Header from '../../../tableHeader';
import SearchSnippetColumn from '../../searchSnippet';

import { Account } from 'types/accounts';

function contains(array: Array<string>, string: string): boolean {
  const index = array.findIndex(item => item === string);
  return index !== -1;
}

export default function useDetailSearchColumns(
  columnsList?: Array<string>,
): Array<Column<Account>> {
  return React.useMemo((): Array<Column<Account>> => {
    if (columnsList) {
      const columns = [];
      if (contains(columnsList, 'Proteus Account ID'))
        columns.push(getIDHeader(Header));
      if (contains(columnsList, 'CID')) columns.push(getCIDHeader(Header));
      if (contains(columnsList, 'SID')) columns.push(getSIDHeader(Header));
      if (contains(columnsList, 'PID')) columns.push(getPIDHeader(Header));
      if (contains(columnsList, 'Field Name'))
        columns.push(getFieldNameHeader(Header));
      if (contains(columnsList, 'Field Label'))
        columns.push(getFieldLabelHeader(Header));
      if (contains(columnsList, 'Account Name'))
        columns.push(getNameHeader(Header));
      if (contains(columnsList, 'Rel Manager'))
        columns.push(getRelManagerHeader(Header));
      if (contains(columnsList, 'Search Snippet'))
        columns.push(getSearchSnippetHeader(Header));
    }
    return [
      getIDHeader(Header),
      getCIDHeader(Header),
      getSIDHeader(Header),
      getPIDHeader(Header),
      getFieldNameHeader(Header),
      getFieldLabelHeader(Header),
      getNameHeader(Header),
      getRelManagerHeader(Header),
      getSearchSnippetHeader(Header),
    ];
  }, [columnsList]);
}

function getIDHeader(
  Header: (
    props: TableInstance<Account> & {
      column: ColumnInstance<Account>;
    },
  ) => JSX.Element,
): Column<Account> {
  return {
    Header: Header,
    accessor: 'AccountId',
    id: 'Proteus Account ID',
  };
}

function getCIDHeader(
  Header: (
    props: TableInstance<Account> & {
      column: ColumnInstance<Account>;
    },
  ) => JSX.Element,
): Column<Account> {
  return {
    Header: Header,
    accessor: 'CID',
    id: 'CID',
  };
}

function getSIDHeader(
  Header: (
    props: TableInstance<Account> & {
      column: ColumnInstance<Account>;
    },
  ) => JSX.Element,
): Column<Account> {
  return {
    Header: Header,
    accessor: 'SID',
    id: 'SID',
  };
}

function getPIDHeader(
  Header: (
    props: TableInstance<Account> & {
      column: ColumnInstance<Account>;
    },
  ) => JSX.Element,
): Column<Account> {
  return {
    Header: Header,
    accessor: 'PID',
    id: 'PID',
  };
}

function getFieldNameHeader(
  Header: (
    props: TableInstance<Account> & {
      column: ColumnInstance<Account>;
    },
  ) => JSX.Element,
): Column<Account> {
  return {
    id: 'Field Name',
    Header: Header,
    accessor: (row: Account) => (row.FieldName ? `${row.FieldName || ''}` : ''),
  };
}
function getFieldLabelHeader(
  Header: (
    props: TableInstance<Account> & {
      column: ColumnInstance<Account>;
    },
  ) => JSX.Element,
): Column<Account> {
  return {
    id: 'Field Label',
    Header: Header,
    accessor: (row: Account) =>
      row.FieldLabel ? `${row.FieldLabel || ''}` : '',
  };
}

function getNameHeader(
  Header: (
    props: TableInstance<Account> & {
      column: ColumnInstance<Account>;
    },
  ) => JSX.Element,
): Column<Account> {
  return {
    id: 'Account Name',
    Header: Header,
    accessor: (row: Account) =>
      row.AccountName ? `${row.AccountName || ''}` : '',
  };
}

function getRelManagerHeader(
  Header: (
    props: TableInstance<Account> & {
      column: ColumnInstance<Account>;
    },
  ) => JSX.Element,
): Column<Account> {
  return {
    id: 'Rel Manager',
    Header: Header,
    accessor: (row: Account) =>
      row.RelationshipManagerName ? `${row.RelationshipManagerName || ''}` : '',
  };
}

function getSearchSnippetHeader(
  Header: (
    props: TableInstance<Account> & {
      column: ColumnInstance<Account>;
    },
  ) => JSX.Element,
): Column<Account> {
  return {
    id: 'Search Snippet',
    Header: Header,
    Cell: SearchSnippetColumn,
  };
}
